import { z } from "zod";

import {
  READ_STEP_MAX_RETRIES,
  StepKey,
  StepResultType,
  type StepContext,
  type WorkflowStep,
} from "@/src/lib/workflows/steps/types";
import { ErrorType } from "@/src/models/error-log";

export enum ReceivingAssignmentScope {
  BASELINE_ONLY = "baseline_only",
  FULL_CAMELBACK = "full_camelback",
  MANUAL_REVIEW = "manual_review",
}

const providerSelectionEvidenceSchema = z.object({
  selectedStep: z
    .enum(["upcoming_appointment", "past_appointment", "usual_provider", "referral_provider", "diagnosis_match"])
    .nullable(),
  upcomingAppointmentCount: z.number().int().nonnegative(),
  pastAppointmentCount: z.number().int().nonnegative(),
  /**
   * Whether the referral went from one of the customer's own departments to another, which inverts
   * provider precedence so the provider named on the referral is checked before appointment
   * history. Defaulted so decisions persisted before this field existed still parse — and `false`
   * is the correct reading for those, since they predate the rule.
   */
  internalReferral: z.boolean().default(false),
  patientPrimaryProvider: z
    .object({
      providerEhrId: z.string(),
      providerName: z.string(),
      providerNpi: z.string().nullable(),
      isDeparted: z.boolean(),
    })
    .nullable()
    .optional(),
  appointmentProviders: z.array(
    z.object({
      providerEhrId: z.string().nullable(),
      providerName: z.string(),
      providerNpi: z.string().nullable(),
      isActiveAtTpr: z.boolean(),
      upcomingAppointmentCount: z.number().int().nonnegative(),
      pastAppointmentCount: z.number().int().nonnegative(),
      qualifyingUpcomingAppointmentCount: z.number().int().nonnegative().optional(),
      qualifyingPastAppointmentCount: z.number().int().nonnegative().optional(),
      earliestUpcomingAppointmentAt: z.string().nullable().optional(),
      earliestUpcomingLocationEhrId: z.string().nullable().optional(),
      mostRecentPastAppointmentAt: z.string().nullable(),
      mostRecentPastLocationEhrId: z.string().nullable().optional(),
    })
  ),
});

export const receivingAssignmentOutputSchema = z.object({
  patientType: z.enum(["established", "new"]),
  providerEhrId: z.string().nullable(),
  providerFamilyName: z.string().nullable(),
  providerGivenName: z.string().nullable(),
  providerName: z.string().nullable(),
  providerNpi: z.string().nullable(),
  targetDepartmentId: z.string().nullable(),
  sourceDepartmentId: z.string().nullable(),
  referralDepartmentId: z.string().nullable(),
  patientPrimaryDepartmentId: z.string().nullable(),
  scope: z.enum(ReceivingAssignmentScope),
  hasUpcomingAppointmentWithin60Days: z.boolean(),
  /** ISO datetime of the patient's soonest upcoming appointment inside that 60-day window; null when
   * there is none. Defaulted so decisions persisted before this field existed still parse. */
  earliestUpcomingAppointmentAt: z.string().nullable().default(null),
  /** Whether that appointment is near enough that even an urgent referral is already satisfied by it
   * (see `TPR_URGENT_UPCOMING_APPOINTMENT_DAYS`). Defaulted for the same back-compat reason — a
   * legacy row reads as "not near", which preserves the old urgent-keeps-scheduling behavior. */
  hasUpcomingAppointmentWithinUrgentWindow: z.boolean().default(false),
  shouldUpdatePatientUsualProvider: z.boolean(),
  /**
   * The two appointment-history facts `shouldUpdatePatientUsualProvider` is derived from, persisted
   * so an ops override can recompute it rather than inherit a decision made about a different
   * provider. Neither is recoverable from the fields above — `patientType` uses a lookback window
   * and counts appointments of any status, while this one means "ever completed, anywhere".
   *
   * `hasAnyPastCompletedAppointment` defaults to `true` for rows written before it existed: that
   * reads as "the patient has been seen", which preserves their existing usual provider. The
   * opposite default would have a legacy override overwrite it.
   */
  hasAnyPastCompletedAppointment: z.boolean().default(true),
  patientPrimaryProviderIsDeparted: z.boolean().default(false),
  manualReviewReasons: z.array(z.string()),
  trace: z.array(z.string()),
  providerSelectionEvidence: providerSelectionEvidenceSchema.optional(),
  /** Non-blocking note for the Athena internal note when the resolved/kept provider may not treat
   * the referral's diagnosis — set whether the provider was auto-switched (new patient) or kept
   * as-is (established patient). Does not affect `scope`/`manualReviewReasons`. */
  providerDiagnosisMismatchNote: z.string().nullable().default(null),
  /** Non-blocking reviewer notices (e.g. the Terrace cardiology/neurology check) surfaced in the
   * sync-instructions UI. Distinct from `manualReviewReasons`, which gates `scope`. */
  advisories: z.array(z.string()).default([]),
});

export type ReceivingAssignmentOutput = z.infer<typeof receivingAssignmentOutputSchema>;

type CreateResolveReceivingAssignmentStepOptions = {
  resolve: (ctx: StepContext) => Promise<ReceivingAssignmentOutput>;
};

export function createResolveReceivingAssignmentStep(
  options: CreateResolveReceivingAssignmentStepOptions
): WorkflowStep {
  return {
    key: StepKey.RESOLVE_RECEIVING_ASSIGNMENT,
    maxRetries: READ_STEP_MAX_RETRIES,
    errorType: ErrorType.PROVIDER_MAPPING,
    async execute(ctx) {
      const output = receivingAssignmentOutputSchema.parse(await options.resolve(ctx));

      return {
        type: StepResultType.COMPLETED,
        output,
      };
    },
  };
}
